<template>

  
  <!-- Modal Overlay -->
  <div v-if="modalVisible" class="modal-overlay" @click="handleModalClose">
    <div class="modal-content" @click.stop>
      <!-- Modal Header -->
      <div class="modal-header">
        <h3>
          NFT Collection ({{ nfts.length }} NFTs)
          <span v-if="debugMode" style="font-size: 12px; color: #00ff00; margin-left: 10px;">[DEBUG] hasMoreNFTs: {{ hasMoreNFTs }}, isLoading: {{ isLoading }}</span>
        </h3>
        <button @click="handleModalClose" class="modal-close">&times;</button>
      </div>
      
      <!-- Modal Body -->
      <div class="modal-body">
        <div class="nft-grid-container">
          <!-- Loading State -->
          <div v-if="isLoading && nfts.length === 0" class="loading-overlay">
            <div class="loading-spinner"></div>
            <p>Loading NFTs...</p>
          </div>

          <!-- Error State -->
          <div v-if="errorMessage" class="error-overlay">
            <div class="error-icon">⚠️</div>
            <p>{{ errorMessage }}</p>
            <button @click="handleRetry" class="retry-button">Retry</button>
          </div>

          <!-- Main Content -->
          <div v-else class="nft-grid-content">
            <!-- Price Loading Indicator (Non-blocking) -->
            <div v-if="isLoadingPrices && nfts.length > 0" class="price-loading-indicator">
              <div class="price-loading-content">
                <div class="loading-spinner-small"></div>
                <span class="price-loading-text">Loading prices: {{ priceLoadingProgress.loaded }}/{{ priceLoadingProgress.total }}</span>
              </div>
            </div>
            <!-- Controls Section -->
            <div class="controls-section">
              <NftSearch 
                :nfts="nfts"
                :search-options="searchOptions"
                :debug-mode="debugMode"
                @search-results="handleSearchResults"
                @search-start="handleSearchStart"
                @search-complete="handleSearchComplete"
              />
              
              <NftSorting 
                :nfts="nfts"
                :debug-mode="debugMode"
                :enable-sorting="!isInitialLoad"
                @sort-results="handleSortResults"
                @sort-start="handleSortStart"
                @sort-complete="handleSortComplete"
              />
            </div>

            <!-- NFT Grid -->
            <div class="nft-grid-main">
              <div v-if="debugMode" style="font-size: 12px; color: #00ff00; margin-bottom: 10px;">
                [DEBUG] NftGrid passing to NftUI: {{ displayNFTs.length }} NFTs, hasMoreNFTs: {{ hasMoreNFTs }}, isLoading: {{ isLoading }}
              </div>
              <NftUI 
                :nfts="displayNFTs"
                :is-loading="isLoading"
                :has-more-nfts="hasMoreNFTs"
                :floor-price-data="floorPriceData"
                :sales-data="salesData"
                :debug-mode="debugMode"
                @nft-select="handleNFTSelect"
                @load-more="handleLoadMore"
                @retry="handleRetry"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- NFT Detail Modal -->
    <NftDetailModal 
      v-model="detailModalVisible"
      :nft="selectedNFT"
      :debug-mode="debugMode"
      @close="handleDetailModalClose"
      @confirm="handleModalConfirm"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import NftSearch from './NftSearch.vue'
import NftSorting from './NftSorting.vue'
import NftUI from './NftUI.vue'
import NftDetailModal from './NftDetailModal.vue'
import { useNftApi } from '../composables/useNftApi'
import type { NFT, FloorPriceResponse } from '../types/nft'

interface SearchOptions {
  loadMore?: () => Promise<void>
  hasMoreNFTs?: boolean
  isLoading?: boolean
  currentPageKey?: string
}

// Props
interface Props {
  modelValue?: boolean
  walletAddress?: string
  debugMode?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  walletAddress: '',
  debugMode: false
})

console.log('[NFT Grid] Props received:', { modelValue: props.modelValue, walletAddress: props.walletAddress, debugMode: props.debugMode })

// Emits
const emit = defineEmits<{
  (e: 'nftSelect', nft: NFT): void
  (e: 'modalOpen'): void
  (e: 'modalClose'): void
  (e: 'update:modelValue', value: boolean): void
  (e: 'close'): void
  (e: 'confirm', nft: NFT): void
  (e: 'error', message: string): void
  (e: 'loadComplete', nfts: NFT[]): void
}>()

// Reactive state
const nfts = ref<NFT[]>([])
const filteredNFTs = ref<NFT[]>([])
const selectedNFT = ref<NFT | null>(null)
const modalVisible = ref(props.modelValue)
const isLoading = ref(false)
const hasMoreNFTs = ref(true)
const errorMessage = ref('')
const currentPageKey = ref<string | undefined>(undefined)
const showSpam = ref(false)
const showAirdrops = ref(false)
const floorPriceData = ref<FloorPriceResponse | null>(null)
const salesData = ref<any | null>(null)
const isSearchActive = ref(false)
const isInitialLoad = ref(false)
const isLoadingPrices = ref(false)
const priceLoadingProgress = ref({ loaded: 0, total: 0 })

// Debug function
const debug = (...args: any[]) => {
  if (props.debugMode) {
    console.log('[NFT Grid]', ...args)
  }
}

// Computed
const displayNFTs = computed(() => {
  // If search is active and we have filtered results, show filtered results
  // Otherwise, show all NFTs
  debug('displayNFTs computed - isSearchActive:', isSearchActive.value, 'filteredNFTs.length:', filteredNFTs.value.length, 'nfts.length:', nfts.value.length)
  
  if (isSearchActive.value && filteredNFTs.value.length > 0) {
    debug('Displaying filtered NFTs:', filteredNFTs.value.length)
    return filteredNFTs.value
  }
  debug('Displaying all NFTs:', nfts.value.length)
  return nfts.value
})

const searchOptions = computed((): SearchOptions => ({
  loadMore: loadMoreNFTs,
  hasMoreNFTs: hasMoreNFTs.value,
  isLoading: isLoading.value,
  currentPageKey: currentPageKey.value
}))

const detailModalVisible = ref(false)

// Initialize composables
const { 
  loadNFTs: apiLoadNFTs, 
  isLoading: apiLoading, 
  error: apiError, 
  cancelRequests 
} = useNftApi(Boolean(props.debugMode)) as {
  loadNFTs: (walletAddress: string, options?: any) => Promise<any>;
  isLoading: { value: boolean };
  error: { value: string | null };
  cancelRequests: () => void;
}

// Handle API errors
watch(() => apiError.value, (newError) => {
  if (newError) {
    errorMessage.value = newError
    emit('error', newError)
  }
})

// Watch for NFTs array changes
watch(() => nfts.value, (newNFTs, oldNFTs) => {
  debug('NFTs array changed from', oldNFTs?.length || 0, 'to', newNFTs?.length || 0)
  if (newNFTs.length === 0 && oldNFTs && oldNFTs.length > 0) {
    debug('WARNING: NFTs array was cleared unexpectedly')
  }
}, { deep: true })

// Methods
const loadNFTs = async (walletAddress: string) => {
  console.log('[NFT Grid] loadNFTs called with wallet:', walletAddress, 'debugMode:', props.debugMode)
  
  if (!walletAddress) {
    const errorMsg = 'No wallet address provided'
    errorMessage.value = errorMsg
    emit('error', errorMsg)
    return
  }

  // Check if modal is still visible before starting to load
  if (!modalVisible.value) {
    debug('Modal is not visible, skipping NFT load')
    return
  }

  debug('Loading NFTs for wallet:', walletAddress)
  isLoading.value = true
  isInitialLoad.value = true
  errorMessage.value = ''
  hasMoreNFTs.value = true // Reset to true at start of loading

  try {
    // Load ALL NFTs first for proper sorting
    debug('Loading ALL NFTs for proper sorting...')
    const allNFTs: NFT[] = []
    let pageKey: string | undefined = undefined
    let hasMore = true
    let pageCount = 0
    
    // Fetch all pages
    while (hasMore && pageCount < 50) { // Safety limit of 50 pages (5000 NFTs)
      // Check if modal is still visible before each page load
      if (!modalVisible.value) {
        debug('Modal closed during loading, stopping pagination')
        break
      }
      
      try {
        const response = await apiLoadNFTs(walletAddress, {
          excludeSpam: !showSpam.value,
          excludeAirdrops: !showAirdrops.value,
          pageKey: pageKey
        })

        if (!response) {
          throw new Error('No response from API')
        }

        allNFTs.push(...(response.ownedNfts || []))
        hasMore = !!response.pageKey
        pageKey = response.pageKey || undefined
        pageCount++
        
        debug(`Loaded page ${pageCount}, total NFTs so far: ${allNFTs.length}`)
      } catch (error) {
        // Check if this is an abort error
        if (error instanceof Error && error.name === 'AbortError') {
          debug('NFT loading aborted during pagination')
          throw error
        }
        throw error
      }
    }
    
    debug(`Loaded ${allNFTs.length} NFTs`)
    console.log('[NFT Grid] Setting NFTs array at:', new Date().toISOString())
    nfts.value = allNFTs
    filteredNFTs.value = allNFTs
    hasMoreNFTs.value = false // No more pagination needed
    debug('Set hasMoreNFTs to false after loading all NFTs')
    debug('NFTs array length after setting:', nfts.value.length)
    debug('Filtered NFTs array length after setting:', filteredNFTs.value.length)
    debug('isSearchActive after loading:', isSearchActive.value)
    debug('displayNFTs computed value after loading:', displayNFTs.value.length)
    currentPageKey.value = undefined
    
    // Set loading to false immediately so NFTs appear in the grid
    isLoading.value = false
    
    // Load collection values for estimated pricing in real-time
    if (allNFTs.length > 0) {
      console.log('[NFT Grid] About to call loadMetadata at:', new Date().toISOString())
      // Start loading prices in the background
      loadMetadata().catch(error => {
        debug('Error loading metadata:', error)
        // Don't show error to user for metadata loading failures
      })
      console.log('[NFT Grid] loadMetadata call initiated at:', new Date().toISOString())
    }
    
    // Reset search state and filtered NFTs to show all NFTs initially
    isSearchActive.value = false
    filteredNFTs.value = []
    debug('Reset search state after loading NFTs')
    
    // Enable sorting immediately - no delay needed
    isInitialLoad.value = false
    debug('Sorting is now enabled')
    
    emit('loadComplete', allNFTs)
  } catch (error) {
    const errorMessageText = error instanceof Error ? error.message : 'Failed to load NFTs'
    debug('Error loading NFTs:', error)
    errorMessage.value = 'Failed to load NFTs. Please try again.'
    emit('error', errorMessageText)
  } finally {
    // Ensure hasMoreNFTs is false if we have loaded NFTs (since we load all at once)
    if (nfts.value.length > 0) {
      hasMoreNFTs.value = false
      debug('Ensuring hasMoreNFTs is false in finally block')
    }
  }
}

const loadMoreNFTs = async () => {
  // Since we now load all NFTs upfront, this function is no longer needed
  // But keeping it for compatibility with existing event handlers
  return
}

const startBackendLogStream = () => {
  try {
    const eventSource = new EventSource('/api/debug/logs')
    
    eventSource.onmessage = (event) => {
      try {
        const logEntry = JSON.parse(event.data)
        if (logEntry.message !== 'keepalive') {
          console.log(`[Backend] ${logEntry.message}`)
        }
      } catch (error) {
        console.error('[Backend] Error parsing log entry:', error)
      }
    }
    
    eventSource.onerror = (error) => {
      console.error('[Backend] Log stream error:', error)
      eventSource.close()
    }
    
    // Store reference to close later if needed
    ;(window as any).backendLogStream = eventSource
  } catch (error) {
    console.error('[Backend] Failed to start log stream:', error)
  }
}

const loadMetadata = async () => {
  console.log('[NFT Grid] loadMetadata function ENTRY at:', new Date().toISOString())
  debug('Loading metadata for existing NFTs - START')
  console.log('[NFT Grid] loadMetadata called at:', new Date().toISOString())
  
  // Start backend log streaming if in debug mode
  if (props.debugMode) {
    startBackendLogStream()
  }
  
  // Test if backend is reachable
  try {
    const testResponse = await fetch('/api/gas-prices', { signal: AbortSignal.timeout(5000) })
    console.log('[NFT Grid] Backend test response:', testResponse.status)
  } catch (error) {
    console.error('[NFT Grid] Backend test failed:', error)
  }
  
  if (nfts.value.length === 0) {
    console.log('[NFT Grid] loadMetadata: No NFTs found, returning early')
    debug('No NFTs to load metadata for')
    return
  }
  
  try {
    console.log('[NFT Grid] Starting NFT filtering at:', new Date().toISOString())
    // Only load metadata for NFTs that don't already have estimated values
    const nftsWithoutMetadata = nfts.value.filter((nft: NFT) => !nft.estimatedValue)
    const contractsNeedingMetadata = [...new Set(nftsWithoutMetadata.map((nft: NFT) => nft.contract?.address).filter(Boolean) as string[])]
    console.log('[NFT Grid] NFT filtering completed at:', new Date().toISOString(), 'Found', contractsNeedingMetadata.length, 'contracts')
    
    if (contractsNeedingMetadata.length === 0) {
      console.log('[NFT Grid] loadMetadata: All NFTs already have metadata, returning early')
      debug('All NFTs already have metadata loaded')
      return
    }
    
    debug('Loading collection values for contracts:', contractsNeedingMetadata.length)
    console.log('[NFT Grid] Starting price loading for', contractsNeedingMetadata.length, 'contracts at:', new Date().toISOString())
    isLoadingPrices.value = true
    priceLoadingProgress.value = { loaded: 0, total: contractsNeedingMetadata.length }
    
        // Process contracts in batches to avoid overwhelming the API
    const batchSize = 5 // Process 5 contracts at a time
    console.log(`[NFT Grid] Starting batch processing for ${contractsNeedingMetadata.length} contracts at:`, new Date().toISOString())
    
    for (let i = 0; i < contractsNeedingMetadata.length; i += batchSize) {
      const batch = contractsNeedingMetadata.slice(i, i + batchSize)
      console.log(`[NFT Grid] Processing batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(contractsNeedingMetadata.length/batchSize)} at:`, new Date().toISOString())
      console.log(`[NFT Grid] Batch ${Math.floor(i/batchSize) + 1} contracts:`, batch)
      
              const batchPromises = batch.map(async (contract) => {
          try {
            console.log(`[NFT Grid] About to start API calls for contract ${contract} at:`, new Date().toISOString())
            console.log(`[NFT Grid] Starting API calls for contract ${contract} at:`, new Date().toISOString())
          // Get floor price
          const floorPriceUrl = new URL(`/api/nft/floor-price/${contract}`, window.location.origin)
          console.log(`[NFT Grid] Making request to: ${floorPriceUrl.toString()}`)
          const floorResponse = await fetch(floorPriceUrl, { signal: AbortSignal.timeout(10000) }) // 10 second timeout
          console.log(`[NFT Grid] Floor price response received for ${contract} at:`, new Date().toISOString(), 'Status:', floorResponse.status)
          const floorData = floorResponse.ok ? await floorResponse.json() : null

          // Get sales data
          const salesUrl = new URL(`/api/nft/sales/${contract}`, window.location.origin)
          console.log(`[NFT Grid] Making sales request to: ${salesUrl.toString()}`)
          const salesResponse = await fetch(salesUrl, { signal: AbortSignal.timeout(10000) }) // 10 second timeout
          console.log(`[NFT Grid] Sales response received for ${contract} at:`, new Date().toISOString(), 'Status:', salesResponse.status)
          const salesData = salesResponse.ok ? await salesResponse.json() : null

          // Update NFTs with this contract's pricing data immediately
          nfts.value = nfts.value.map((nft: NFT) => {
            if (nft.contract?.address !== contract || nft.estimatedValue) return nft
            
            let estimatedValue: NFT['estimatedValue'] = null
            
            // Handle floor price data with error checking
            if (floorData) {
              if (floorData.error) {
                // Show error state instead of loading forever
                estimatedValue = {
                  value: null,
                  currency: 'ETH',
                  confidence: 'low' as const,
                  source: `${floorData.marketplace} Floor Price`,
                  reason: floorData.error,
                  displayValue: `Error: ${floorData.error}`
                }
              } else if (floorData.floorPrice !== null && floorData.floorPrice !== undefined) {
                // Valid floor price data
                const marketplaceName = floorData.marketplace === 'looksrare' ? 'LooksRare' : 
                                      floorData.marketplace === 'opensea' ? 'OpenSea' : 
                                      floorData.marketplace
                
                estimatedValue = {
                  value: floorData.floorPrice,
                  currency: floorData.priceCurrency || 'ETH',
                  confidence: 'medium' as const,
                  source: `${marketplaceName} Floor Price`,
                  displayValue: `${floorData.floorPrice} ${floorData.priceCurrency || 'ETH'}`
                }
              }
            }
            
            // If we have sales data, use it to improve the estimate
            if (salesData?.nftSales && salesData.nftSales.length > 0) {
              const recentSales = salesData.nftSales
              const avgPrice = recentSales.reduce((sum: number, sale: any) => sum + (sale.sellerFee || 0), 0) / recentSales.length
              
              if (!estimatedValue || avgPrice > (estimatedValue.value || 0)) {
                estimatedValue = {
                  value: avgPrice,
                  currency: 'ETH',
                  confidence: 'high' as const,
                  source: 'Recent Sales',
                  recentSales: recentSales.length,
                  displayValue: `${avgPrice.toFixed(4)} ETH`
                }
              }
            }
            
            return {
              ...nft,
              estimatedValue
            }
          })
          
          // Update progress
          priceLoadingProgress.value.loaded++
          console.log(`[NFT Grid] Loaded pricing for contract ${contract}, progress: ${priceLoadingProgress.value.loaded}/${priceLoadingProgress.value.total} at: ${new Date().toISOString()}`)
          debug(`Loaded pricing for contract ${contract}, progress: ${priceLoadingProgress.value.loaded}/${priceLoadingProgress.value.total}`)
          
          return { contract, floorData, salesData }
        } catch (error) {
          debug(`Error loading pricing for contract ${contract}:`, error)
          // Update progress even on error
          priceLoadingProgress.value.loaded++
          return { contract, floorData: null, salesData: null }
        }
      })
      
      // Wait for current batch to complete before processing next batch
      console.log(`[NFT Grid] Waiting for batch ${Math.floor(i/batchSize) + 1} to complete...`)
      await Promise.all(batchPromises)
      console.log(`[NFT Grid] Batch ${Math.floor(i/batchSize) + 1} completed at:`, new Date().toISOString())
    }
    
    debug('All pricing data loaded successfully')
  } catch (error) {
    // Check if this is an abort error
    if (error instanceof Error && error.name === 'AbortError') {
      debug('Metadata loading aborted')
      throw error
    }
    debug('Error loading metadata:', error)
    // Continue without metadata if there's an error
  } finally {
    isLoadingPrices.value = false
    priceLoadingProgress.value = { loaded: 0, total: 0 }
  }
}

const handleSearchResults = (results: NFT[]) => {
  debug('Search results received:', results.length, 'total NFTs:', nfts.value.length, 'isInitialLoad:', isInitialLoad.value)
  debug('Search results called from:', new Error().stack?.split('\n')[2] || 'unknown')
  
  // Don't process search results during initial load
  if (isInitialLoad.value) {
    debug('Ignoring search results during initial load')
    return
  }
  
  // Check if this is a "clear search" event (results match all NFTs)
  if (results.length === nfts.value.length && results.length > 0) {
    // This is likely a clear search event
    isSearchActive.value = false
    filteredNFTs.value = []
    debug('Cleared search, showing all NFTs')
  } else if (results.length === 0) {
    // No search results
    filteredNFTs.value = []
    isSearchActive.value = true
    debug('No search results found')
  } else {
    // This is a search with results
    filteredNFTs.value = results
    isSearchActive.value = true
    debug('Search results applied:', results.length)
  }
}

const handleSearchStart = () => {
}

const handleSearchComplete = (_message?: string) => {
}

const handleSortResults = (results: NFT[]) => {
  console.log('[NFT Grid] Sort results received:', results.length)
  debug('Sort results received:', results.length, 'isInitialLoad:', isInitialLoad.value)
  
  // Don't process sort results during initial load
  if (isInitialLoad.value) {
    debug('Ignoring sort results during initial load')
    return
  }
  
  // If search is active, update filtered results
  // Otherwise, update the main NFTs array
  if (isSearchActive.value) {
    filteredNFTs.value = results
  } else {
    nfts.value = results
  }
}

const handleSortStart = () => {
  console.log('[NFT Grid] Sort started')
  debug('Sort started')
}

const handleSortComplete = (_message?: string) => {
  console.log('[NFT Grid] Sort completed:', _message)
  debug('Sort completed:', _message)
}

const handleNFTSelect = (nft: NFT) => {
  console.log('[NFT Grid] NFT selected:', nft.name || nft.title || 'Untitled NFT')
  selectedNFT.value = nft
  detailModalVisible.value = true
  emit('nftSelect', nft)
}

const handleLoadMore = () => {
  loadMoreNFTs()
}

const handleRetry = () => {
  errorMessage.value = ''
  if (props.walletAddress) {
    loadNFTs(props.walletAddress).catch(error => {
      const errorMsg = error instanceof Error ? error.message : 'Failed to load NFTs'
      errorMessage.value = errorMsg
      emit('error', errorMsg)
    })
  } else {
    const errorMsg = 'No wallet address available for retry'
    errorMessage.value = errorMsg
    emit('error', errorMsg)
  }
}



const handleModalClose = () => {
  console.log('[NFT Grid] Closing NFT grid modal')
  // Cancel any ongoing requests when modal is closed
  try {
    cancelRequests()
    debug('Cancelled ongoing requests on modal close')
  } catch (error) {
    debug('Error cancelling requests:', error)
  }
  // Reset loading state and clear any error messages
  isLoading.value = false
  errorMessage.value = ''
  detailModalVisible.value = false
  selectedNFT.value = null
  emit('close')
}

const handleDetailModalClose = () => {
  console.log('[NFT Grid] Closing detail modal')
  detailModalVisible.value = false
  selectedNFT.value = null
}



const handleModalConfirm = (nft: NFT) => {
  try {
    emit('confirm', nft)
    // Cancel any ongoing requests when modal is confirmed
    try {
      cancelRequests()
      debug('Cancelled ongoing requests on modal confirm')
    } catch (error) {
      debug('Error cancelling requests:', error)
    }
    // Reset loading state
    isLoading.value = false
    modalVisible.value = false
    selectedNFT.value = null
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to confirm NFT selection'
    errorMessage.value = errorMsg
    emit('error', errorMsg)
  }
}



const resetAndReload = () => {
  nfts.value = []
  filteredNFTs.value = []
  selectedNFT.value = null
  modalVisible.value = false
  isLoading.value = false
  hasMoreNFTs.value = true
  errorMessage.value = ''
  currentPageKey.value = undefined
  isSearchActive.value = false
  isInitialLoad.value = false
  
  if (props.walletAddress) {
    loadNFTs(props.walletAddress).catch(error => {
      const errorMsg = error instanceof Error ? error.message : 'Failed to reload NFTs'
      errorMessage.value = errorMsg
      emit('error', errorMsg)
    })
  } else {
    const errorMsg = 'No wallet address available for reload'
    errorMessage.value = errorMsg
    emit('error', errorMsg)
  }
}

// Expose methods for parent components
defineExpose({
  loadNFTs,
  loadMetadata,
  resetAndReload,
  handleNFTSelect: (nft: NFT) => {
    selectedNFT.value = nft
    detailModalVisible.value = true
    emit('nftSelect', nft)
  },
  handleLoadMore: () => {
    loadMoreNFTs().catch(error => {
      const errorMsg = error instanceof Error ? error.message : 'Failed to load more NFTs'
      errorMessage.value = errorMsg
      emit('error', errorMsg)
    })
  },
  handleRetry,
  handleModalClose: () => {
    // Cancel any ongoing requests when modal is closed
    try {
      cancelRequests()
      debug('Cancelled ongoing requests on exposed handleModalClose')
    } catch (error) {
      debug('Error cancelling requests:', error)
    }
    // Reset loading state and clear any error messages
    isLoading.value = false
    errorMessage.value = ''
    modalVisible.value = false
    detailModalVisible.value = false
    selectedNFT.value = null
    emit('update:modelValue', false)
    emit('close')
  },
  handleModalConfirm
})

// Watch for API loading state
watch(() => apiLoading.value, (loading) => {
  isLoading.value = loading
})

// Watch for API errors
watch(() => apiError.value, (error) => {
  if (error) {
    errorMessage.value = error
  }
})

// Watch for wallet address changes
watch(() => props.walletAddress, (newAddress, oldAddress) => {
  console.log('[NFT Grid] Wallet address changed from:', oldAddress, 'to:', newAddress)
  
  // If wallet address changed, clear existing NFTs and error state
  if (oldAddress && newAddress && oldAddress !== newAddress) {
    debug('Wallet address changed, clearing existing NFTs and error state')
    nfts.value = []
    filteredNFTs.value = []
    errorMessage.value = ''
    hasMoreNFTs.value = true
    currentPageKey.value = undefined
    isSearchActive.value = false
    isInitialLoad.value = false
  }
  
  // Don't load NFTs immediately when wallet address changes
  // Only load when modal is opened
}, { immediate: true })

// Watch for modelValue changes to sync modal visibility
watch(() => props.modelValue, (newValue) => {
  console.log('[NFT Grid] modelValue changed to:', newValue, 'debugMode:', props.debugMode)
  modalVisible.value = newValue
  
  if (!newValue) {
    // Modal is being closed - cancel any ongoing requests
    try {
      cancelRequests()
      debug('Cancelled ongoing requests on modelValue change to false')
    } catch (error) {
      debug('Error cancelling requests:', error)
    }
    // Reset loading state and clear any error messages
    isLoading.value = false
    errorMessage.value = ''
  } else {
    // Modal is being opened - clear any previous error messages
    errorMessage.value = ''
    // Load NFTs if modal is opening and we don't have any NFTs loaded yet
    if (props.walletAddress && nfts.value.length === 0) {
      console.log('[NFT Grid] Loading NFTs for wallet on modal open:', props.walletAddress)
      loadNFTs(props.walletAddress)
    } else if (props.walletAddress && nfts.value.length > 0) {
      console.log('[NFT Grid] Modal opened with NFTs already loaded')
      // Metadata loading is now handled automatically when NFTs are loaded
    } else {
      console.log('[NFT Grid] Modal opened but no wallet address or no NFTs loaded')
    }
  }
}, { immediate: true })

// Watch for detail modal visibility changes
watch(() => detailModalVisible.value, (newValue) => {
  console.log('[NFT Grid] Detail modal visibility changed to:', newValue)
  if (!newValue) {
    selectedNFT.value = null
  }
})

// Lifecycle
onMounted(() => {
  console.log('[NFT Grid] Component mounted, debugMode:', props.debugMode)
})
// Clean up
onUnmounted(() => {
  try {
    cancelRequests()
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Error during cleanup'
    console.error('Error during cleanup:', errorMsg)
  }
})

</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
}

.modal-content {
  background: var(--card-bg);
  border-radius: 8px;
  max-width: 95vw;
  max-height: 95vh;
  width: 95vw;
  height: 95vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--text-primary);
  transition: background-color 0.3s, color 0.3s;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--background-secondary);
  transition: background-color 0.3s, border-color 0.3s;
}

.modal-header h3 {
  margin: 0;
  color: var(--text-primary);
  transition: color 0.3s;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
}

.modal-close:hover {
  color: var(--text-primary);
}

.modal-body {
  flex: 1;
  overflow: hidden;
  padding: 20px;
  position: relative;
}

.nft-grid-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(var(--card-bg-rgb, 255, 255, 255), 0.9);
  z-index: 100;
  color: var(--text-primary);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.price-loading-indicator {
  position: fixed;
  top: 20px;
  right: 20px;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  color: var(--text-primary);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  animation: slideInRight 0.3s ease-out;
}

.price-loading-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-color);
  border-top: 2px solid var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.price-loading-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(var(--card-bg-rgb, 255, 255, 255), 0.9);
  z-index: 100;
  text-align: center;
  color: var(--error-color);
}

.error-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.retry-button {
  margin-top: 20px;
  padding: 10px 20px;
  background: var(--error-color);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: var(--profit-negative);
}

.nft-grid-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.controls-section {
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--card-bg);
}

.nft-grid-main {
  flex: 1;
  overflow: auto;
  height: 100%;
}

.modal-controls {
  display: flex;
  gap: 15px;
  align-items: center;
  padding: 15px;
  background: var(--card-bg);
  border-radius: 6px;
}

.modal-content {
  height: 100%;
  overflow: hidden;
}

/* Responsive Design */
@media (max-width: 768px) {
  .controls-section {
    padding: 15px;
  }
  
  .modal-controls {
    flex-direction: column;
    gap: 10px;
  }
}
</style> 